import IntlTelInput from 'intl-tel-input/react';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/styles';
import { FormikProps } from 'formik';
import { ObjStrKeyStrVal, TextInputInterface } from '../types';

type PhoneFieldProps = TextInputInterface & {
  formik: FormikProps<ObjStrKeyStrVal>;
  showErrorMessages?: boolean;
};

const INTL_PH_UTILITY_SCRIPT = `https://cdn.jsdelivr.net/npm/intl-tel-input@${intlTelInput.version}/build/js/utils.js`;
const DEFAULT_COUNTRY = 'us';

const LpePhoneField = ({
  formik,
  isTouchedAndError,
  field,
  textFieldClassNames,
  showErrorMessages = false,
}: PhoneFieldProps) => {
  const hasError = isTouchedAndError || !!formik.errors[field.parameterName];
  const errorClass = hasError ? 'errorBorder' : '';
  const errorId = `${field.parameterName}-error`;

  const handlePhNumChange = (value: string) => {
    if (!formik.touched[field.parameterName] && value.length > 0) {
      formik.setFieldTouched(field.parameterName, true, false);
    }
    if (formik.touched[field.parameterName] && value.length === 0) {
      formik.setFieldTouched(field.parameterName, false, false);
    }
    // INTENTIONAL: Direct mutation instead of setFieldValue to avoid re-render issues.
    // IntlTelInput manages its own internal state. Using setFieldValue triggers Formik
    // re-renders on every keystroke, causing input focus loss and cursor jumping.
    // The value is correctly included in formik.values at submit time.
    formik.values[field.parameterName] = value;
  };

  const handlePhNumValidate = (valid: boolean) => {
    // Store validity state for Yup schema to check (enables form submission blocking)
    formik.values._phoneInvalid = !valid;

    // Use only the phone field's validity, not the entire form's validity
    if (
      valid &&
      formik.touched[field.parameterName] &&
      formik.errors[field.parameterName]
    ) {
      formik.setFieldError(field.parameterName, '');
    }
    if (
      !valid &&
      formik.touched[field.parameterName] &&
      !formik.errors[field.parameterName]
    ) {
      formik.setFieldError(field.parameterName, 'Invalid phone number');
    }
  };

  return (
    <>
      <IntlTelInput
        onChangeNumber={handlePhNumChange}
        onChangeValidity={handlePhNumValidate}
        initOptions={{
          loadUtilsOnInit: INTL_PH_UTILITY_SCRIPT,
          strictMode: true,
          autoPlaceholder: 'polite',
          initialCountry: DEFAULT_COUNTRY,
          countryOrder: [DEFAULT_COUNTRY, 'ca', 'gb'],
          containerClass: 'intlTelInput',
          placeholderNumberType: 'FIXED_LINE',
          formatAsYouType: true,
          formatOnDisplay: true,
          fixDropdownWidth: true,
          nationalMode: true,
          validationNumberType: 'MOBILE',
        }}
        inputProps={{
          id: field.parameterName,
          className: `iti__tel-input ${textFieldClassNames.mainField} ${errorClass}`,
          'aria-label': 'Phone Number',
          'aria-required': true,
          'aria-invalid': hasError,
          'aria-describedby': showErrorMessages && hasError ? errorId : undefined,
        }}
      />
      {showErrorMessages && hasError && formik.errors[field.parameterName] && (
        <div
          id={errorId}
          role="alert"
          className="errorMessage phoneErrorMessage"
        >
          {formik.errors[field.parameterName] as string}
        </div>
      )}
    </>
  );
};

export default LpePhoneField;
